// Package imports:
import React, { useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import cx from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUpFromBracket, faTrashCan } from '@fortawesome/pro-solid-svg-icons';

// UI element imports:
import Button from '../../../ui-elements/Button/Button';

export interface ImagePreview {
    url: string;
    name: string;
    width: number;
    height: number;
    size: number;
}

interface IImageUploadFieldProps {
    label: string;
    description?: string;
    preview: ImagePreview | null;
    setPreview: (preview: ImagePreview | null) => void;
    error: string;
    setError: (error: string) => void;
    setFile: (file: File | null) => void;
    maxWidthPx: number;
    maxHeightPx: number;
    maxSizeKB: number;
    previewHeight?: number;
}

const ACCEPTED_EXTENSIONS = ['.jpg', '.jpeg', '.png'];
const ACCEPTED_MIME_TYPES = ['image/jpeg', 'image/png', 'image/jpg'];

const ImageUploadField: React.FC<IImageUploadFieldProps> = ({
    label,
    description,
    preview,
    setPreview,
    error,
    setError,
    setFile,
    maxWidthPx,
    maxHeightPx,
    maxSizeKB,
    previewHeight = 50,
}) => {
    const maxSizeBytes = maxSizeKB * 1024;

    const isAcceptedFileType = useCallback((file: File): boolean => {
        const extension = '.' + file.name.split('.').pop()?.toLowerCase();
        return ACCEPTED_EXTENSIONS.includes(extension) || ACCEPTED_MIME_TYPES.includes(file.type);
    }, []);

    const readFileAsDataURL = useCallback((file: File): Promise<string> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = () => reject(new Error('Ekki tókst að lesa skrá.'));
            reader.readAsDataURL(file);
        });
    }, []);

    const validateImage = useCallback((file: File, dataUrl: string): Promise<{ valid: boolean; width: number; height: number; error?: string }> => {
        return new Promise((resolve) => {
            if (!isAcceptedFileType(file)) {
                const extension = file.name.split('.').pop()?.toUpperCase() || 'óþekkt';
                resolve({
                    valid: false,
                    width: 0,
                    height: 0,
                    error: `Skráargerð .${extension} er ekki studd. Vinsamlegast notaðu JPG, JPEG eða PNG.`,
                });
                return;
            }

            const img = new Image();
            img.onload = () => {
                const errors: string[] = [];
                const fileSizeKB = Math.round(file.size / 1024);

                if (img.width > maxWidthPx || img.height > maxHeightPx) {
                    errors.push(
                        `Myndin er ${img.width} × ${img.height} px en hámark er ${maxWidthPx} × ${maxHeightPx} px.`
                    );
                }

                if (file.size > maxSizeBytes) {
                    errors.push(
                        `Skráarstærð er ${fileSizeKB} kb en hámark er ${maxSizeKB} kb.`
                    );
                }

                if (errors.length > 0) {
                    resolve({ valid: false, width: img.width, height: img.height, error: errors.join(' ') });
                } else {
                    resolve({ valid: true, width: img.width, height: img.height });
                }
            };
            img.onerror = () => {
                resolve({ valid: false, width: 0, height: 0, error: 'Ekki tókst að lesa mynd. Vinsamlegast reyndu aðra skrá.' });
            };
            img.src = dataUrl;
        });
    }, [isAcceptedFileType, maxWidthPx, maxHeightPx, maxSizeBytes, maxSizeKB]);

    const onDrop = useCallback(async (acceptedFiles: File[]) => {
        if (acceptedFiles.length === 0) return;

        const file = acceptedFiles[0];
        let dataUrl: string;
        try {
            dataUrl = await readFileAsDataURL(file);
        } catch {
            setError('Ekki tókst að lesa skrá. Vinsamlegast reyndu aftur.');
            return;
        }

        const result = await validateImage(file, dataUrl);
        const newPreview: ImagePreview = {
            url: dataUrl,
            name: file.name,
            width: result.width,
            height: result.height,
            size: file.size,
        };

        setPreview(newPreview);
        setFile(result.valid ? file : null);
        setError(result.valid ? '' : result.error || '');
    }, [readFileAsDataURL, validateImage, setPreview, setFile, setError]);

    const handleDelete = useCallback(() => {
        setFile(null);
        setPreview(null);
        setError('');
    }, [setFile, setPreview, setError]);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        multiple: false,
        accept: { 'image/jpeg': ['.jpg', '.jpeg'], 'image/png': ['.png'] },
    });

    return (
        <div className="image-upload-field">
            <label className="image-upload-field__label">{label}</label>
            {description && (
                <p className="image-upload-field__description">{description}</p>
            )}

            <div className="image-upload-field__size-requirements">
                <div className="image-upload-field__size-item">
                    <span className="image-upload-field__size-bullet" />
                    <span>Hámark: {maxWidthPx} × {maxHeightPx} px</span>
                </div>
                <div className="image-upload-field__size-item">
                    <span className="image-upload-field__size-bullet" />
                    <span>Hámarks þyngd: {maxSizeKB} kb</span>
                </div>
            </div>

            {/* Show dropzone only if no valid preview */}
            {(!preview || error) && (
                <div
                    {...getRootProps()}
                    className={cx('image-upload-field__dropzone', {
                        'image-upload-field__dropzone--active': isDragActive,
                        'image-upload-field__dropzone--error': !!error,
                    })}
                >
                    <input {...getInputProps()} />
                    <FontAwesomeIcon
                        icon={faArrowUpFromBracket}
                        className="image-upload-field__dropzone-icon"
                    />
                    <div className="image-upload-field__dropzone-text">
                        <p className="image-upload-field__dropzone-instruction">Smelltu hér eða dragðu</p>
                        <p className="image-upload-field__dropzone-filetypes">JPG, JPEG, PNG</p>
                        <p className="image-upload-field__dropzone-instruction">myndina þína á þetta svæði.</p>
                    </div>
                </div>
            )}

            {/* Valid preview with delete */}
            {preview && !error && (
                <div className="image-upload-field__preview-row">
                    <img
                        src={preview.url}
                        alt={label}
                        className="image-upload-field__preview-img"
                        style={{ height: previewHeight }}
                    />
                    <div className="image-upload-field__preview-meta">
                        <span>{preview.width} × {preview.height} px</span>
                        <span>{Math.round(preview.size / 1024)} kb</span>
                        <Button
                            buttonType="secondary"
                            size="sm"
                            icon={faTrashCan}
                            onClick={handleDelete}
                        >
                            Eyða mynd
                        </Button>
                    </div>
                </div>
            )}

            {/* Error state with rejected preview */}
            {error && preview && (
                <div className="image-upload-field__error-info">
                    <div className="image-upload-field__preview-meta image-upload-field__preview-meta--error">
                        <span>{preview.width} × {preview.height} px</span>
                        <span>{Math.round(preview.size / 1024)} kb</span>
                    </div>
                    <p className="image-upload-field__error-text">{error}</p>
                </div>
            )}
        </div>
    );
};

export default ImageUploadField;
